import React from 'react';

import { ApprovalStatus, getStatusDisplayName, Preset } from '../../main/components/Preset/models';
import { Role, useAuthStore } from '../../../features/authentication';

import { ReactComponent as ArrowDownIcon } from '../../../assets/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../../../assets/arrow_up.svg';
import { formatCreateDate } from '../../../utils';
import StatusDropdown from './StatusDropdown';

const PresetCard = ({
  preset,
  fetchPresets,
  statusDropdownOpen,
  setStatusDropdownOpen,
}: {
  preset: Preset;
  fetchPresets: () => void;
  statusDropdownOpen: number | null;
  setStatusDropdownOpen: (statusDropdownOpen: number | null) => void;
}) => {
  const { username, hasRole } = useAuthStore();

  const openPrivate = (id: number) => {
    window.location.href = `/${id}`;
  };

  const showStatusOptions = (presetId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the default click behavior
    setStatusDropdownOpen(statusDropdownOpen === presetId ? null : presetId);
  };

  const updateMarket = (id: number) => {
    window.location.href = `/preset/${id}`;
  };

  const getDiscount = (discount: number) => {
    const formattedDiscount = Math.abs(discount / 2).toFixed(1);
    const sign = discount < 0 ? '-' : discount > 0 ? '+' : '';
    return `${sign}${formattedDiscount}%`;
  };

  const getFee = (fee: number) => {
    return (fee * 0.01).toFixed(2);
  };

  const getPeriod = (period: string) => {
    if (period === 'Monthly') {
      return '/mo';
    } else if (period === 'Annually') {
      return '/an';
    } else {
      return '';
    }
  };

  const isOwnPreset = preset.user === username;

  let indicator = getStatusDisplayName(preset.approvalStatus, preset.overallDiscount < 0); // Default to pending
  let indicatorStyle = styles.pendingIndicator;

  if (preset.approvalStatus === ApprovalStatus.APPROVED_APPROVER) {
    indicatorStyle = styles.approvedIndicator;
  } else if (preset.approvalStatus === ApprovalStatus.REJECTED_APPROVER) {
    indicatorStyle = styles.rejectedIndicator;
  } else if (preset.approvalStatus === ApprovalStatus.PRIVATE) {
    indicatorStyle = styles.privateIndicator;
  } else if (preset.approvalStatus === ApprovalStatus.APPROVED_CRO) {
    if (preset.overallDiscount >= 0) {
      indicatorStyle = styles.approvedIndicator;
    } else {
      indicatorStyle = styles.pendingIndicator;
    }
  } else if (preset.approvalStatus === ApprovalStatus.REJECTED_CRO) {
    indicatorStyle = styles.rejectedIndicator;
  }

  return (
    <div
      key={preset.id}
      data-testid="preset-item"
      data-preset-id={preset.id}
      data-preset-name={preset.name}
      data-preset-owner={preset.user}
      data-is-own-preset={isOwnPreset}
      data-approval-status={preset.approvalStatus}
      style={{
        ...styles.card,
      }}
      onClick={() =>
        preset.approvalStatus === ApprovalStatus.PRIVATE ? openPrivate(preset.id) : updateMarket(preset.id)
      }
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 3, display: 'flex', flexDirection: 'column' }}>
          <div style={styles.itemHeader}>
            <span style={styles.itemName} data-testid="preset-name">
              {preset.name}
            </span>
          </div>
          {hasRole([Role.ADMIN, Role.VIEWER, Role.CRO]) && (
            <div style={styles.itemParam} data-testid="preset-user-field">
              <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>User</div> {preset.user}
            </div>
          )}
          <div style={styles.itemParam}>
            <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Company</div> {preset.company}
          </div>
          <div style={styles.itemParam}>
            <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Adjustment Set</div> {preset.set}
          </div>
          <div style={styles.itemParam}>
            <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Paint Provided</div> {preset.paintProvided}
          </div>
          <div
            style={{
              ...styles.itemParam,
              color: 'rgba(16, 29, 59, 0.6)',
              paddingTop: '0.8vh',
            }}
          >
            Notes
          </div>
          <div style={{ ...styles.itemParam, paddingTop: '0.2vh' }}>{preset.reason}</div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1.6vh' }}>
            <div style={styles.itemParam}>
              <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Created on</div> {formatCreateDate(preset.created)}
            </div>
            <div style={styles.itemParam}>
              <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Updated on</div> {formatCreateDate(preset.lastUpdated)}
            </div>
          </div>
        </div>
        <div style={styles.itemBadgeContainer}>
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Margin
            </div>
            {preset.margin}%
          </div>
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Discount
            </div>
            {preset.overallDiscount}%
          </div>
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Paint
            </div>
            {getDiscount(preset.paintDiscount)}
          </div>
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Clean
            </div>
            {getDiscount(preset.cleanDiscount)}
          </div>
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Carpet Clean
            </div>
            {getDiscount(preset.carpetCleanDiscount)}
          </div>
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Maintenance
            </div>
            {getDiscount(preset.maintenanceDiscount)}
          </div>
          {preset.saasFeeEnabled === 'Yes' && (
            <div style={styles.itemBadge}>
              <div
                style={{
                  fontWeight: '400',
                  fontSize: '1.2vh',
                }}
              >
                Unit Count
              </div>
              {preset.saasContractedUnits.toLocaleString()}
            </div>
          )}
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Platform Fee
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.4vh' }}>
              {preset.saasFeeEnabled === 'Yes' ? (
                <>
                  ${getFee(preset.saasUnitFee)}
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: '1.2vh',
                      color: 'rgba(16, 29, 59, 0.6)',
                    }}
                  >
                    {getPeriod(preset.saasFeePeriod)}
                  </span>
                </>
              ) : (
                'N/A'
              )}
            </div>
          </div>
        </div>
      </div>

      {/*for proper alignment */}
      <div style={{ height: '100%' }}></div>

      {/*for spacing */}
      <div style={{ height: '1.6vh' }}></div>

      <div
        style={{
          ...styles.indicator,
          ...indicatorStyle,
        }}
        data-testid="preset-status-indicator"
        onClick={e => showStatusOptions(preset.id, e)}
      >
        {indicator}
        {statusDropdownOpen === preset.id ? (
          <ArrowUpIcon
            style={{
              width: '2.4vh',
              height: '2.4vh',
            }}
          />
        ) : (
          <ArrowDownIcon
            style={{
              width: '2.4vh',
              height: '2.4vh',
            }}
          />
        )}
        {statusDropdownOpen === preset.id && (
          <StatusDropdown preset={preset} setStatusDropdownOpen={setStatusDropdownOpen} fetchPresets={fetchPresets} />
        )}
      </div>
    </div>
  );
};

const styles = {
  card: {
    padding: '1.6vh',
    borderRadius: '1.6vh',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column' as 'column',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    fontSize: '1.4vh',
    fontWeight: '400',
    color: 'rgba(16, 29, 59, 1)',
    cursor: 'pointer',
  },
  itemHeader: {
    display: 'flex',
    flexDirection: 'row' as 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemName: {
    color: 'rgba(16, 29, 59, 1)',
    fontSize: '1.6vh',
    fontWeight: '700',
  },
  itemParam: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    paddingTop: '0.8vh',
    gap: '0.4vh',
    textAlign: 'left' as 'left',
  },
  indicator: {
    padding: '0.9vh 1.6vh',
    borderRadius: '0.8vh',
    fontWeight: '700',
    fontSize: '1.2vh',
    position: 'relative' as 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between' as 'space-between',
    gap: '0.4vh',
    cursor: 'default',
  },
  approvedIndicator: {
    color: 'rgba(0, 171, 155, 1)',
    backgroundColor: 'rgba(0, 171, 155, 0.05)',
  },
  rejectedIndicator: {
    color: 'rgba(243, 119, 102, 1)',
    backgroundColor: 'rgba(243, 119, 102, 0.05)',
  },
  pendingIndicator: {
    color: 'rgba(4, 129, 200, 1)',
    backgroundColor: 'rgba(4, 129, 200, 0.05)',
  },
  pendingYellowIndicator: {
    color: 'rgba(255, 193, 7, 1)',
    backgroundColor: 'rgba(255, 193, 7, 0.05)',
  },
  privateIndicator: {
    color: 'rgba(16, 29, 59, 0.4)',
    backgroundColor: 'rgba(16, 29, 59, 0.05)',
  },
  itemBadgeContainer: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    gap: '0.4vh',
    paddingTop: '0.8vh',
    flex: 2,
  },
  itemBadge: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row' as 'row',
    alignItems: 'start',
    justifyContent: 'space-between' as 'space-between',
    padding: '0.8vh',
    borderRadius: '0.8vh',
    color: 'rgba(16, 29, 59, 1)',
    backgroundColor: 'rgba(16, 29, 59, 0.05)',
    boxSizing: 'border-box' as 'border-box',
    gap: '0.2vh',
    fontWeight: '700',
  },
};

export default PresetCard;
